import { authFetch } from '../auth/authFetch';
import { redirectToSignIn, requireIdentityHeaders } from '../auth/identity';
import { WORKSPACE_IDS } from '../auth/workspaces';
import { readFileResponse } from './fileResponse';

/**
 * Investor workspace client. Every call is a GET against /v1/investor/*;
 * the backend answers with platform-wide data and rejects any mutation.
 */
const API_BASE_URL =
  (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(/\/$/, '');

function buildQueryString(query) {
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

function getContextHeaders() {
  return requireIdentityHeaders(WORKSPACE_IDS.INVESTOR);
}

function extractErrorMessage(payload, status) {
  const fallback = `Request failed (${status})`;
  if (!payload || typeof payload !== 'object') return fallback;
  if (typeof payload.message === 'string' && payload.message.trim()) return payload.message;
  if (typeof payload.detail === 'string' && payload.detail.trim()) return payload.detail;
  return fallback;
}

async function parseResponse(response) {
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    let message = `Request failed (${response.status})`;
    try {
      message = extractErrorMessage(await response.json(), response.status);
    } catch {
      // empty or non-JSON body
    }
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  if (response.status === 204) return null;
  return response.json();
}

async function get(path, query) {
  const response = await authFetch(`${API_BASE_URL}${path}${buildQueryString(query)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', ...getContextHeaders() },
  });
  return parseResponse(response);
}

async function getBlob(path, query) {
  const response = await authFetch(`${API_BASE_URL}${path}${buildQueryString(query)}`, {
    method: 'GET',
    headers: { ...getContextHeaders() },
  });
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    let message = `Request failed (${response.status})`;
    try {
      message = extractErrorMessage(await response.json(), response.status);
    } catch {
      // empty or non-JSON body
    }
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  return readFileResponse(response);
}

export function getInvestorOverview() {
  return get('/v1/investor/overview');
}

export function listInvestorPackages({
  page = 1,
  pageSize = 20,
  q,
  status,
  programId,
  organizationId,
} = {}) {
  return get('/v1/investor/packages', {
    page,
    page_size: pageSize,
    q,
    status,
    program_id: programId,
    organization_id: organizationId,
  });
}

export function getInvestorPackage(packageId) {
  return get(`/v1/investor/packages/${encodeURIComponent(packageId)}`);
}

export function getInvestorFileCopyContext(packageId, fileId) {
  return get(
    `/v1/investor/packages/${encodeURIComponent(packageId)}/files/${encodeURIComponent(fileId)}/copy-context`
  );
}

/** Watermarked labelled copy of a package file (never the preserved original). */
export function listInvestorFileMembers(packageId, fileId) {
  return get(
    `/v1/investor/packages/${encodeURIComponent(packageId)}/files/${encodeURIComponent(fileId)}/members`
  );
}

export function downloadInvestorFileRendition(
  packageId,
  fileId,
  { disposition = 'inline', member } = {}
) {
  return getBlob(
    `/v1/investor/packages/${encodeURIComponent(packageId)}/files/${encodeURIComponent(fileId)}/rendition`,
    { disposition, member }
  );
}

export function listInvestorActivity({
  page = 1,
  pageSize = 20,
  eventType,
  organizationId,
  packageId,
  dateFrom,
  dateTo,
  q,
} = {}) {
  return get('/v1/investor/activity', {
    page,
    page_size: pageSize,
    event_type: eventType,
    organization_id: organizationId,
    package_id: packageId,
    date_from: dateFrom,
    date_to: dateTo,
    q,
  });
}

export function listInvestorActivityEventTypes() {
  return get('/v1/investor/activity/event-types');
}

export function listInvestorAudit({
  page = 1,
  pageSize = 20,
  action,
  organizationId,
  dateFrom,
  dateTo,
} = {}) {
  return get('/v1/investor/audit', {
    page,
    page_size: pageSize,
    action,
    organization_id: organizationId,
    date_from: dateFrom,
    date_to: dateTo,
  });
}

export function listInvestorOrganizations({ page = 1, pageSize = 50, type } = {}) {
  return get('/v1/investor/organizations', { page, page_size: pageSize, type });
}

export function listInvestorPrograms({ page = 1, pageSize = 50, organizationId } = {}) {
  return get('/v1/investor/programs', {
    page,
    page_size: pageSize,
    organization_id: organizationId,
  });
}
